/**
 * Price clocks: Alex's view of when each brand's MAP and promo changes take
 * effect (flag `vendor_price_clock`). A brand with a clock has its date-only
 * sheet rows stored at the vendor's own instant (Sony 00:00 Pacific, Canon
 * 23:59 Eastern), so every store switches at the same moment; a brand without
 * one keeps switching at each store's midnight.
 */
import React, { useMemo, useState } from 'react';
import {
  Box,
  Button,
  FormControlLabel,
  IconButton,
  MenuItem,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { AddOutlined, DeleteOutline, EditOutlined, RestartAltOutlined } from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ConfirmDialog,
  EmptyState,
  FormDialog,
  InlineMessage,
  LoadingState,
  PageContainer,
  PageHeader,
  SectionCard,
  StatusChip,
} from '../../components/ui';
import { useToast } from '../../contexts/ToastContext';
import { STORE_TIME_ZONES } from '../../constants/storeTimeZones';
import { brandIdentityKey } from '../../utils/brandIdentity';
import { todayIn, wallClockIn, zoneShortName } from '../../utils/vendorClockDisplay';
import {
  vendorPriceClockApi,
  type VendorPriceClock,
  type VendorPriceClockInput,
  type VendorPriceClockProgram,
} from '../../services/vendorPriceClockApi';

const QUERY_KEY = ['admin-vendor-price-clocks'];
const TIME_RE = /^([01]\d|2[0-3]):[0-5]\d(:[0-5]\d)?$/;
const COMPARE_ZONES = ['America/New_York', 'America/Los_Angeles'];

const emptyDraft = (): VendorPriceClockInput => ({
  brandName: '',
  timeZone: 'America/New_York',
  startTime: '00:00',
  endTime: '23:59:59',
  enabled: true,
  programs: [],
  sourceNote: '',
});

const zoneLabel = (zone: string) => STORE_TIME_ZONES.find((z) => z.value === zone)?.label || zone;

/** "03:00 ET · 00:00 PT" for the clock's start time today. */
function onStoreClocks(clock: Pick<VendorPriceClock, 'timeZone'>, time: string): string {
  const date = todayIn(clock.timeZone);
  return COMPARE_ZONES.map((zone) => `${wallClockIn(date, time, clock.timeZone, zone)} ${zoneShortName(zone)}`).join(' · ');
}

export const BrandVendorsPriceClocksPage: React.FC = () => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const [editing, setEditing] = useState<{ brandKey: string | null; draft: VendorPriceClockInput } | null>(null);
  const [resetTarget, setResetTarget] = useState<VendorPriceClock | null>(null);

  const query = useQuery({ queryKey: QUERY_KEY, queryFn: () => vendorPriceClockApi.list() });
  const clocks = useMemo(() => query.data?.clocks || [], [query.data]);

  const save = useMutation({
    mutationFn: ({ brandKey, draft }: { brandKey: string; draft: VendorPriceClockInput }) => vendorPriceClockApi.save(brandKey, draft),
    onSuccess: () => {
      showToast('Price clock saved', 'success');
      setEditing(null);
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    },
    onError: (err: Error) => showToast(err.message, 'error'),
  });
  const reset = useMutation({
    mutationFn: (brandKey: string) => vendorPriceClockApi.reset(brandKey),
    onSuccess: (body) => {
      showToast(body.message || 'Reset', 'success');
      setResetTarget(null);
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    },
    onError: (err: Error) => showToast(err.message, 'error'),
  });

  const draft = editing?.draft;
  const setDraft = (patch: Partial<VendorPriceClockInput>) =>
    setEditing((current) => (current ? { ...current, draft: { ...current.draft, ...patch } } : current));
  const setProgram = (index: number, patch: Partial<VendorPriceClockProgram>) =>
    setDraft({ programs: (draft?.programs || []).map((p, i) => (i === index ? { ...p, ...patch } : p)) });
  const draftKey = editing?.brandKey ?? brandIdentityKey(draft?.brandName || '');
  const draftError = (() => {
    if (!draft) return null;
    if (!draft.brandName.trim()) return 'Brand is required';
    if (!TIME_RE.test(draft.startTime) || !TIME_RE.test(draft.endTime)) return 'Times are 24-hour HH:MM or HH:MM:SS';
    if ((draft.programs || []).some((p) => !p.match.trim() || (p.startTime && !TIME_RE.test(p.startTime)) || (p.endTime && !TIME_RE.test(p.endTime)))) {
      return 'Each program needs match text and valid times';
    }
    if (!editing?.brandKey && clocks.some((c) => c.brandKey === draftKey)) return 'This brand already has a clock; edit it instead';
    return null;
  })();

  const submit = () => {
    if (!draft || draftError) return;
    const programs = (draft.programs || []).map((p) => ({
      match: p.match.trim(),
      ...(p.label?.trim() ? { label: p.label.trim() } : {}),
      ...(p.timeZone ? { timeZone: p.timeZone } : {}),
      ...(p.startTime ? { startTime: p.startTime } : {}),
      ...(p.endTime ? { endTime: p.endTime } : {}),
    }));
    save.mutate({
      brandKey: draftKey,
      draft: {
        ...draft,
        brandName: draft.brandName.trim(),
        programs,
        sourceNote: draft.sourceNote?.trim() || undefined,
        confidence: draft.confidence || undefined,
      },
    });
  };

  return (
    <PageContainer>
      <PageHeader
        title="Price clocks"
        description="When each brand's MAP and promo changes take effect. A date-only sheet row of a brand listed here starts and ends at the vendor's own time, so every store switches at the same moment. Brands not listed switch at each store's midnight."
        actions={
          <Button variant="contained" startIcon={<AddOutlined />} onClick={() => setEditing({ brandKey: null, draft: emptyDraft() })}>
            Add brand
          </Button>
        }
      />

      {query.data && (
        <Box sx={{ mb: 2 }}>
          {query.data.flagEnabled ? (
            <InlineMessage severity="success">
              The vendor price clock flag is on: new sheets for these brands are stored at the vendor's time.
            </InlineMessage>
          ) : (
            <InlineMessage severity="info">
              The vendor price clock flag is off, so sheets are still stored as dates. Run the backfill script, then turn the flag on in Feature flags.
            </InlineMessage>
          )}
        </Box>
      )}

      <SectionCard dense title="Brand clocks" subtitle="Times are the vendor's wall clock. Daylight time is applied automatically.">
        {query.isLoading ? (
          <LoadingState message="Loading price clocks..." />
        ) : query.isError ? (
          <InlineMessage severity="error">{(query.error as Error).message}</InlineMessage>
        ) : clocks.length === 0 ? (
          <EmptyState size="compact" title="No price clocks" description="Every brand switches at each store's midnight." />
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Brand</TableCell>
                <TableCell>Vendor time zone</TableCell>
                <TableCell>Starts</TableCell>
                <TableCell>Ends</TableCell>
                <TableCell>Source</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right" />
              </TableRow>
            </TableHead>
            <TableBody>
              {clocks.map((clock) => (
                <TableRow key={clock.brandKey} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight={600}>{clock.brandName}</Typography>
                    {(clock.programs || []).length > 0 && (
                      <Typography variant="caption" color="text.secondary">
                        {(clock.programs || []).map((p) => p.label || p.match).join(', ')}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>{zoneLabel(clock.timeZone)}</TableCell>
                  <TableCell>
                    <Typography variant="body2">{clock.startTime} on the start date</Typography>
                    <Typography variant="caption" color="text.secondary">{onStoreClocks(clock, clock.startTime)}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">Through {clock.endTime} on the end date</Typography>
                    <Typography variant="caption" color="text.secondary">{onStoreClocks(clock, clock.endTime)}</Typography>
                  </TableCell>
                  <TableCell>
                    <Tooltip title={clock.sourceNote || ''}>
                      <Stack direction="row" spacing={0.5}>
                        <StatusChip size="small" tone={clock.origin === 'seed' ? 'neutral' : 'info'} label={clock.origin === 'seed' ? 'Researched default' : 'Edited'} />
                        {clock.confidence && (
                          <StatusChip
                            size="small"
                            variant="outlined"
                            tone={clock.confidence === 'high' ? 'positive' : clock.confidence === 'medium' ? 'caution' : 'problem'}
                            label={`${clock.confidence} confidence`}
                          />
                        )}
                      </Stack>
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    <StatusChip size="small" tone={clock.enabled ? 'positive' : 'neutral'} label={clock.enabled ? 'On' : 'Off'} />
                  </TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                    <Tooltip title="Edit">
                      <IconButton
                        size="small"
                        aria-label={`Edit ${clock.brandName}`}
                        onClick={() =>
                          setEditing({
                            brandKey: clock.brandKey,
                            draft: {
                              brandName: clock.brandName,
                              timeZone: clock.timeZone,
                              startTime: clock.startTime,
                              endTime: clock.endTime,
                              enabled: clock.enabled,
                              programs: clock.programs || [],
                              sourceNote: clock.sourceNote || '',
                              confidence: clock.confidence,
                            },
                          })
                        }
                      >
                        <EditOutlined fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    {clock.origin === 'stored' && (
                      <Tooltip title="Reset">
                        <IconButton size="small" aria-label={`Reset ${clock.brandName}`} onClick={() => setResetTarget(clock)}>
                          <RestartAltOutlined fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </SectionCard>

      <FormDialog
        open={!!editing}
        title={editing?.brandKey ? `Edit ${draft?.brandName} clock` : 'Add a price clock'}
        onClose={() => setEditing(null)}
        onSubmit={submit}
        submitLabel="Save"
        submitDisabled={!!draftError}
        loading={save.isPending}
        maxWidth="md"
      >
        {draft && (
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField
              label="Brand"
              value={draft.brandName}
              onChange={(e) => setDraft({ brandName: e.target.value })}
              disabled={!!editing?.brandKey}
              helperText={editing?.brandKey ? undefined : 'As it appears on the pricing sheets, e.g. Sony'}
              required
            />
            <TextField select label="Vendor time zone" value={draft.timeZone} onChange={(e) => setDraft({ timeZone: e.target.value })}>
              {STORE_TIME_ZONES.map((z) => (
                <MenuItem key={z.value} value={z.value}>{z.label}</MenuItem>
              ))}
            </TextField>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                label="Start time"
                type="time"
                value={draft.startTime}
                onChange={(e) => setDraft({ startTime: e.target.value })}
                inputProps={{ step: 1 }}
                helperText="First moment on the start date"
                fullWidth
              />
              <TextField
                label="End time"
                type="time"
                value={draft.endTime}
                onChange={(e) => setDraft({ endTime: e.target.value })}
                inputProps={{ step: 1 }}
                helperText="Live through this minute on the end date"
                fullWidth
              />
            </Stack>
            {TIME_RE.test(draft.startTime) && (
              <Typography variant="body2" color="text.secondary">
                Starts {onStoreClocks(draft, draft.startTime)}; ends {TIME_RE.test(draft.endTime) ? onStoreClocks(draft, draft.endTime) : '-'} (today's offsets).
              </Typography>
            )}
            <Stack direction="row" spacing={2} alignItems="center">
              <FormControlLabel control={<Switch checked={draft.enabled} onChange={(e) => setDraft({ enabled: e.target.checked })} />} label="Use this clock" />
              <TextField
                select
                label="Confidence"
                value={draft.confidence || ''}
                onChange={(e) => setDraft({ confidence: (e.target.value || undefined) as VendorPriceClockInput['confidence'] })}
                sx={{ minWidth: 180 }}
              >
                <MenuItem value="">Not set</MenuItem>
                <MenuItem value="high">High</MenuItem>
                <MenuItem value="medium">Medium</MenuItem>
                <MenuItem value="low">Low</MenuItem>
              </TextField>
            </Stack>
            <TextField
              label="Source"
              value={draft.sourceNote || ''}
              onChange={(e) => setDraft({ sourceNote: e.target.value })}
              helperText="The vendor document or email these times come from"
              multiline
              minRows={2}
            />
            <Box>
              <Typography variant="overline">Program overrides</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                A program whose match text appears in the sheet's category, price type or file name uses its own times.
              </Typography>
              <Stack spacing={1}>
                {(draft.programs || []).map((program, index) => (
                  <Stack key={index} direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems={{ md: 'center' }}>
                    <TextField size="small" label="Match" value={program.match} onChange={(e) => setProgram(index, { match: e.target.value })} required />
                    <TextField size="small" label="Label" value={program.label || ''} onChange={(e) => setProgram(index, { label: e.target.value })} />
                    <TextField
                      size="small"
                      select
                      label="Zone"
                      value={program.timeZone || ''}
                      onChange={(e) => setProgram(index, { timeZone: e.target.value || undefined })}
                      sx={{ minWidth: 160 }}
                    >
                      <MenuItem value="">Brand zone</MenuItem>
                      {STORE_TIME_ZONES.map((z) => (
                        <MenuItem key={z.value} value={z.value}>{z.label}</MenuItem>
                      ))}
                    </TextField>
                    <TextField size="small" label="Start" type="time" value={program.startTime || ''} onChange={(e) => setProgram(index, { startTime: e.target.value || undefined })} inputProps={{ step: 1 }} InputLabelProps={{ shrink: true }} />
                    <TextField size="small" label="End" type="time" value={program.endTime || ''} onChange={(e) => setProgram(index, { endTime: e.target.value || undefined })} inputProps={{ step: 1 }} InputLabelProps={{ shrink: true }} />
                    <IconButton aria-label="Remove program" onClick={() => setDraft({ programs: (draft.programs || []).filter((_, i) => i !== index) })}>
                      <DeleteOutline fontSize="small" />
                    </IconButton>
                  </Stack>
                ))}
                <Box>
                  <Button size="small" startIcon={<AddOutlined />} onClick={() => setDraft({ programs: [...(draft.programs || []), { match: '' }] })}>
                    Add program
                  </Button>
                </Box>
              </Stack>
            </Box>
            {draftError && <InlineMessage severity="warning">{draftError}</InlineMessage>}
          </Stack>
        )}
      </FormDialog>

      <ConfirmDialog
        open={!!resetTarget}
        title={`Reset ${resetTarget?.brandName || ''} clock?`}
        message="Drops the saved clock. A researched brand returns to its default; any other brand goes back to switching at each store's midnight. Rows already imported keep their times."
        confirmLabel="Reset"
        confirmColor="warning"
        loading={reset.isPending}
        onConfirm={() => resetTarget && reset.mutate(resetTarget.brandKey)}
        onClose={() => setResetTarget(null)}
      />
    </PageContainer>
  );
};

export default BrandVendorsPriceClocksPage;
